import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

/**
 * Landing Page Component
 * Modern landing page with hero section, features, and call-to-action
 */
const Landing: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      setShowSuccess(true);
      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
    }
  }, [searchParams]);

  useEffect(() => {
    // Handle hash navigation (e.g., /#features)
    const hash = window.location.hash;
    if (hash === '#features') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const featuresSection = document.getElementById('features');
        if (featuresSection) {
          featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      {/* Success Message */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Email verified successfully! You can now log in.</span>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-neutral-900 to-neutral-950"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <Link to="/" className="block">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 hover:text-cyan-400 transition-colors duration-200 cursor-pointer">
                The <span className="text-cyan-400">Traders</span>
              </h1>
            </Link>
            <p className="text-xl md:text-2xl text-neutral-300 mb-2 max-w-3xl mx-auto">
              Advanced algorithmic trading platform with AI-powered insights, real-time analytics, and comprehensive risk management.
            </p>
            {/* Trial messaging */}
            <p className="text-sm text-gray-400 mb-8 max-w-3xl mx-auto">
              7-day free trial · $39.99/month after · Cancel anytime
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center px-8 py-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Get Started
              </Link>
              <Link
                to="/auth"
                className="inline-flex items-center px-8 py-4 border border-neutral-600 hover:border-neutral-500 text-neutral-300 hover:text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Sign In
              </Link>
              <Link
                to="/learn-more"
                className="inline-flex items-center px-8 py-4 border border-neutral-600 hover:border-neutral-500 text-neutral-300 hover:text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Powerful Trading Features
            </h2>
            <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
              Everything you need for successful algorithmic trading
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-neutral-800 p-8 rounded-lg border border-neutral-700">
              <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Real-time Analytics</h3>
              <p className="text-neutral-400">
                Advanced charts, technical indicators, and real-time market data to make informed trading decisions.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-neutral-800 p-8 rounded-lg border border-neutral-700">
              <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">AI-Powered Insights</h3>
              <p className="text-neutral-400">
                Machine learning algorithms provide market analysis, sentiment detection, and trading recommendations.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-neutral-800 p-8 rounded-lg border border-neutral-700">
              <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Risk Management</h3>
              <p className="text-neutral-400">
                Comprehensive risk controls, position limits, and automated safeguards to protect your capital.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-900/20 to-neutral-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Trading?
          </h2>
          <p className="text-xl text-neutral-400 mb-8">
            Join thousands of traders who trust The Traders platform for their algorithmic trading needs.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center px-8 py-4 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors duration-200"
          >
            Create Your Account
          </Link>
        </div>
      </section>

      {/* Disclaimer and Legal Links */}
      <footer className="bg-neutral-900 border-t border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Investment Disclaimer */}
          <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-red-400 mb-2">
                  Investment Risk Warning
                </h3>
                <p className="text-red-300 text-sm">
                  Trading involves substantial risk of loss and is not suitable for all investors. 
                  Past performance does not guarantee future results. You may lose some or all of your invested capital. 
                  Only invest money you can afford to lose. The Traders is not responsible for any financial losses incurred.
                </p>
              </div>
            </div>
          </div>

          {/* Legal Links */}
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="text-neutral-400 text-sm mb-4 sm:mb-0">
              © 2025 The Traders. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <Link
                to="/learn-more"
                className="text-neutral-400 hover:text-white text-sm transition-colors duration-200"
              >
                Learn More
              </Link>
              <Link
                to="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-400 hover:text-white text-sm transition-colors duration-200"
              >
                Privacy Policy & Terms
              </Link>
              <a
                href="mailto:support@thetraders.ai"
                className="text-neutral-400 hover:text-white text-sm transition-colors duration-200"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
